import React, { useState } from 'react';
import cogoToast from 'cogo-toast';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import ApiURL from '../../api/ApiURL';
import axios from 'axios';

const BookingReportModal = ({ show, tcData, onClose }) => {

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [tcId, setTCId] = useState('');

    //Export data to excel file with formatting
    const exportToExcel = async (data) => {
        if (!data || data.length === 0) {
            cogoToast.warn("No data found to export", { position: "bottom-center" });
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Booking Report');

        /* ================= HEADER ================= */
        worksheet.columns = [
            { header: 'Booking No', key: 'BookingNo', width: 18 },
            { header: 'Booking Date', key: 'BookingDate', width: 15 },
            { header: 'Journey Start', key: 'JourneyStartDate', width: 18 },
            { header: 'Journey End', key: 'JourneyEndDate', width: 18 },
            { header: 'Travel Circuit', key: 'TravelCircuitName', width: 20 },
            { header: 'Full Name', key: 'FullName', width: 25 },
            { header: 'No. of Guest', key: 'NoOfGuests', width: 14 },
            { header: 'Payable Amount', key: 'PayableAmount', width: 14 },
            { header: 'Paid Amount', key: 'PaidAmount', width: 14 },
            { header: 'Due Amount', key: 'DueAmount', width: 14 },
            { header: 'Profit %', key: 'ProfitMarginPercent', width: 12 },
            { header: 'Booking Type', key: 'BookingType', width: 16 },
            { header: 'Mobile', key: 'Mobile', width: 15 },
            { header: 'Email', key: 'Email', width: 25 },
            { header: 'State', key: 'StateName', width: 15 },
            { header: 'Place Of Supply', key: 'PlaceOfSupply', width: 18 },
            { header: 'Trip Category', key: 'TripCategory', width: 16 },
            { header: 'Specialist', key: 'SpecialistName', width: 18 },
            { header: 'GSTIN', key: 'GSTIN', width: 18 },
            { header: 'PAN', key: 'PAN', width: 14 },
            { header: 'Status', key: 'BookingStatus', width: 14 }
        ];

        /* ================= FREEZE HEADER ================= */
        worksheet.views = [{ state: 'frozen', ySplit: 1 }];

        /* ================= STYLE HEADER ================= */
        worksheet.getRow(1).eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '4472C4' }
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        /* ================= DATA ================= */
        data.forEach(item => {
            worksheet.addRow({
                ...item,
                FullName: `${item.FirstName || ''} ${item.LastName || ''}`.trim()
            });
        });

        /* ================= BORDER ALL CELLS ================= */
        worksheet.eachRow(row => {
            row.eachCell(cell => {
                cell.border = {
                    top: { style: 'thin' },
                    bottom: { style: 'thin' },
                    left: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });
        });

        /* ================= DOWNLOAD ================= */
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        saveAs(blob, 'Booking_Report.xlsx');
    };

    //Reset search data
    const onReset = () => {
        setStartDate('');
        setEndDate('');
        setTCId('');
    }

    //Close Modal
    const onCloseModal = () => {
        onReset();
        onClose();
    }

    //Fetch api data
    const getReportData = async (e) => {
        e.preventDefault();

        if (!startDate || !endDate) {
            cogoToast.error("Start & End Date required!", { position: "bottom-center" });
            return;
        }

        try {
            const response = await axios.post(ApiURL.GetBookingReportData, {
                StartDate: startDate,
                EndDate: endDate,
                TravelCircuitId: tcId || null
            });

            if (response.data.StatusCode === 200) {
                exportToExcel(response.data.DataResult);
            } else if (response.data.StatusCode === 404) {
                cogoToast.warn("No data found", { position: "bottom-center" });
            }
            else if (response.data.StatusCode === 500) {
                cogoToast.error("Request Failed!", { position: "bottom-center" });
            }

        } catch {
            cogoToast.error("Request Failed!", { position: "bottom-center" });
        }
    };

    if (!show) return null;

    return (
        <div className="modal show-modal modalWrapper">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">

                    <div className="modal-header">
                        <h3>Booking Report</h3>
                        <i className="fa-regular fa-circle-xmark text-danger" onClick={onCloseModal}></i>
                    </div>

                    <div className="modal-body">
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label>Start Date <span className='text-danger'>*</span></label>
                                <input type="date" className="form-control-sm w-100"
                                    value={startDate}
                                    onChange={e => setStartDate(e.target.value)} />
                            </div>

                            <div className="col-md-4">
                                <label>End Date <span className='text-danger'>*</span></label>
                                <input type="date" className="form-control-sm w-100"
                                    value={endDate}
                                    onChange={e => setEndDate(e.target.value)} />
                            </div>

                            <div className="col-md-4">
                                <label>Travel Circuit</label>
                                <select className="form-control-sm w-100"
                                    value={tcId}
                                    onChange={e => setTCId(e.target.value)}>
                                    <option value="">-- Select --</option>
                                    {tcData.map(tc => (
                                        <option key={tc.TravelCircuitId} value={tc.TravelCircuitId}>
                                            {tc.TravelCircuitName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-outline-primary" onClick={getReportData}>
                            Download Report
                        </button>
                        <button className="btn btn-outline-secondary" onClick={onReset}>
                            Reset
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default BookingReportModal;
